import * as angular from 'angular';
import * as echarts from 'echarts';
import * as _ from 'lodash';

export type IChartOptions = echarts.EChartsOption & {
    demo?: boolean;
};

export type IChartSeriesOptions = echarts.SeriesOption;
export type IChartDatasetOptions = echarts.DatasetComponentOption;

interface IChartScope extends angular.IScope {
    ref: number;
    type: string;
    labels: number[];
    series: Array<{
        name: string;
        options?: IChartSeriesOptions;
    }>;
    data: Array<{
        values: Array<string[] | number[]>;
        options?: IChartDatasetOptions;
    }>;
    dataB: Array<{
        values: Array<string[] | number[]>;
        options?: IChartDatasetOptions;
    }>;
    options?: IChartOptions;
}

class ChartDirective implements angular.IDirective {
    readonly replace = true;
    readonly restrict = 'E';
    readonly scope = {
        ref: '=',
        type: '=',
        labels: '=',
        series: '=',
        data: '=',
        dataB: '=',
        options: '='
    };

    readonly template = '<div class="fc-chart"></div>';

    readonly link: angular.IDirectiveLinkFn;

    constructor(private readonly window: angular.IWindowService) {
        this.link = this._link.bind(this);
    }

    private _link(scope: IChartScope, element: JQuery): void {
        // Create the chart instance
        const chart = echarts.init(element[0]);

        // Apply any initial configuration available
        this.configure(scope, element, chart);

        // Watch for changes on the component scope so the chart can be updated
        scope.$watch(
            () => scope.ref,
            () => this.resize(scope, element, chart)
        );

        scope.$watchGroup(
            [
                () => scope.type,
                () => scope.labels,
                () => scope.series,
                () => scope.data,
                () => scope.dataB,
                () => scope.options
            ],
            () => this.configure(scope, element, chart)
        );

        // Bind to the window resize to update the chart size
        this.window.addEventListener('resize', () => this.resize(scope, element, chart));
    }

    private configure(scope: IChartScope, element: JQuery, chart: echarts.ECharts): void {
        if (scope.labels && scope.series && scope.data) {
            const chartSeries = [];
            const chartDatasets: echarts.DatasetComponentOption[] = _.map(scope.data, data => ({
                source: data.values
            }));

            // Build the datasets and series
            let datasetIndex = chartDatasets.length;

            for (let index = 0; index < scope.data.length; index++) {
                const data = scope.data[index];

                chartDatasets.push({
                    ...data.options,
                    fromDatasetIndex: index
                });
            }

            // The series are built after the data sets so the correct data set indexes can be used
            for (const series of scope.series) {
                if (series.options?.data) {
                    chartSeries.push({
                        type: scope.type,
                        name: series.name,
                        ...series.options
                    });
                } else {
                    chartSeries.push({
                        type: scope.type,
                        name: series.name,
                        ...series.options,
                        datasetIndex: datasetIndex++
                    });
                }
            }

            // Configure the chart
            const xAxisOptions = scope.options?.xAxis;
            const yAxisOptions = scope.options?.yAxis;
            const tooltipOptions = scope.options?.tooltip;

            chart.setOption(
                {
                    ...scope.options,
                    xAxis: {
                        type: 'category',
                        ...xAxisOptions,
                        axisTick: {
                            show: false
                        },
                        axisLine: {
                            show: true,
                            onZero: false,
                            lineStyle: {
                                opacity: 0.25
                            }
                        },
                        splitLine: {
                            show: false
                        }
                    },
                    yAxis: {
                        type: 'value',
                        ...yAxisOptions,
                        axisTick: {
                            show: false
                        },
                        axisLine: {
                            show: true,
                            onZero: false,
                            lineStyle: {
                                opacity: 0.25
                            }
                        },
                        splitLine: {
                            show: false
                        }
                    },
                    tooltip: {
                        ...tooltipOptions,
                        trigger: 'axis'
                    },
                    legend: {
                        show: false
                    },
                    grid: {
                        top: 50,
                        right: 50,
                        bottom: 50,
                        left: 50
                    },
                    series: chartSeries,
                    dataset: chartDatasets
                },
                {
                    notMerge: true
                }
            );

            this.resize(scope, element, chart);
        }
    }

    private resize(scope: IChartScope, element: JQuery, chart: echarts.ECharts): void {
        // Calculate the chart size based on the container element
        // Use setTimeout to ensure the calculation is appended to the end of the UI stack
        this.window.setTimeout(() => {
            const width = element.width();
            const height = width / 2;

            // Adjust the element height
            if (width > 0) {
                element.height(height);
                chart.resize();
            }

            // If in demo mode render the demo text
            // Do this after the resize because we need to know the height / width
            if (scope.options?.demo) {
                const coreOptions = chart.getOption();
                chart.setOption({
                    ...this.configureDemoWatermark(height, width, coreOptions)
                });
            }
        });
    }

    private configureDemoWatermark(
        height: number,
        width: number,
        options: echarts.EChartsCoreOption
    ): any {
        // Set up a canvas background
        const image = document.createElement('canvas');

        image.height = height;
        image.width = width;

        // Add the text the canvas context
        const ctx = image.getContext('2d');
        const x = width / 2;
        const y = height / 2;
        const size = width - width * 0.8;

        ctx.font = `${size}px ${options.textStyle.fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';

        ctx.translate(x, y);

        ctx.fillText('DEMO', 0, 0);

        return {
            backgroundColor: {
                type: 'pattern',
                image,
                repeat: 'no-repeat'
            }
        };
    }
}

const ChartDirectiveFactory: angular.IDirectiveFactory = (window: angular.IWindowService) =>
    new ChartDirective(window);

ChartDirectiveFactory.$inject = ['$window'];

export { ChartDirectiveFactory };
